'use client'

import type { WishlistContextProps } from '@maestro/commerce-blocks/providers/wishlist/wishlist.types'

import { useTranslations } from 'next-intl'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { getWishlist } from '@maestro/commerce-blocks/client/api/wishlist'
import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import {
  addProductToWishlist as addProductToWishlistAction,
  clearWishlist as clearWishlistAction,
  removeProductFromWishlist as removeProductFromWishlistAction,
} from '@maestro/commerce-blocks/server/actions/wishlist'
import { useA11y } from '@maestro/core/a11y/a11y.context'
import { createLogger } from '@maestro/core/logger'
import { AuthStatus, type Product } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:commerce-blocks:wishlist')

const WishlistContext = createContext<WishlistContextProps | undefined>(
  undefined
)

export const WishlistProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const t = useTranslations('commerce-blocks')
  const { showSuccess, showError } = useNotificationContext()
  const { announce } = useA11y()
  const { session } = useMaestroSession()
  const isLoggedIn = useMemo(() => {
    return !!session && session.authContext?.authStatus === AuthStatus.ACTIVE
  }, [session])
  const companyId = session?.user?.company?.id

  const [wishlist, setWishlist] = useState<string[]>([])
  // Wishlisted products are org-unit scoped, so switching company has to re-run
  // the fetch.
  const syncedCompanyRef = useRef<string | null>(null)
  const {
    favorites: { favoritesImplementation },
  } = theme()

  // Wishlist fetch upon login and upon company switch
  useEffect(() => {
    // `''` is not a valid company id, so it safely keys "logged in without a
    // company"; `null` stays reserved for "not fetched yet".
    const syncKey = companyId ?? ''

    const syncWishlist = async () => {
      if (!isLoggedIn) {
        syncedCompanyRef.current = null
        // Whatever the ended session held must not stay on screen for whoever
        // uses this browser next.
        setWishlist((current) => (current.length === 0 ? current : []))
        return
      }

      if (syncedCompanyRef.current === syncKey) {
        return
      }

      syncedCompanyRef.current = syncKey

      try {
        const response = await getWishlist({})

        // A newer fetch (another switch, or a logout) took over while this
        // request was in flight — do not let it write a stale wishlist.
        if (syncedCompanyRef.current !== syncKey) {
          return
        }

        if (response.success && response.data) {
          setWishlist(response.data.productIds ?? [])
        }
      } catch (error) {
        log.error('Error fetching wishlist on login', { error })
        showError(t('wishlist.sync-error'))
      }
    }

    if (favoritesImplementation === 'simple') {
      syncWishlist()
    }
  }, [isLoggedIn, companyId, showError, t, favoritesImplementation])

  const addProductToWishlist = useCallback(
    async (product: Product) => {
      if (!isLoggedIn || wishlist.includes(product.id)) {
        return
      }

      try {
        const response = await addProductToWishlistAction(product.id)

        if (!response.success) {
          showError(t('wishlist.add-product-error'))
          return
        }

        setWishlist([...wishlist, product.id])
        showSuccess(t('wishlist.product-added'))
        announce(
          t('wishlist.product-added-announce', { product: product.name })
        )
      } catch (error) {
        log.error('Error adding product to wishlist', { error, product })
        showError(t('wishlist.add-product-error'))
      }
    },
    [announce, isLoggedIn, showError, showSuccess, t, wishlist]
  )

  const removeProductFromWishlist = useCallback(
    async (productId: string, productName?: string) => {
      if (!isLoggedIn) {
        return
      }

      try {
        const response = await removeProductFromWishlistAction(productId)

        if (!response.success) {
          showError(t('wishlist.remove-product-error'))
          return
        }

        setWishlist(wishlist.filter((item) => item !== productId))
        if (productName) {
          showSuccess(t('wishlist.product-removed', { product: productName }))
          announce(
            t('wishlist.removed-from-wishlist', { product: productName })
          )
        }
      } catch (error) {
        log.error('Error removing product from wishlist', {
          error,
          productId,
        })
        showError(t('wishlist.remove-product-error'))
      }
    },
    [announce, isLoggedIn, showError, showSuccess, t, wishlist]
  )

  const clearWishlist = useCallback(async () => {
    if (!isLoggedIn || wishlist.length === 0) {
      return
    }

    try {
      const response = await clearWishlistAction(wishlist)

      if (response.success) {
        setWishlist([])
        showSuccess(t('wishlist.all-deleted'))
        return
      }

      showError(t('wishlist.clear-error'))
    } catch (error) {
      log.error('Error clearing wishlist', { error })
      showError(t('wishlist.clear-error'))
    }
  }, [isLoggedIn, showError, showSuccess, t, wishlist])

  const isProductInWishlist = useCallback(
    (productId: string) => {
      return wishlist.some((item) => item === productId)
    },
    [wishlist]
  )

  const wishlistCount = useMemo(() => {
    return wishlist?.length || 0
  }, [wishlist])

  const toggleProductInWishlist = useCallback(
    async (product: Product) => {
      const isInWishlist = wishlist.some((item) => item === product.id)
      if (isInWishlist) {
        await removeProductFromWishlist(product.id, product.name)
      } else {
        await addProductToWishlist(product)
      }
    },
    [addProductToWishlist, removeProductFromWishlist, wishlist]
  )

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addProductToWishlist,
        removeProductFromWishlist,
        toggleProductInWishlist,
        clearWishlist,
        isProductInWishlist,
        wishlistCount,
      }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export const useWishlistContext = () => {
  const context = useContext(WishlistContext)
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
}
